import { Link } from 'react-router-dom';
import Navbar from './home/components/Navbar';
import Footer from './home/components/Footer';
import RegisterLink from '../components/RegisterLink';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageSeo } from '../hooks/usePageSeo';
import { LinkedText } from '../components/LinkedText';
import { getKeywordPage, type Localized } from '../constants/keywordPages';

type KeywordLandingProps = {
  slug: string;
};

export default function KeywordLanding({ slug }: KeywordLandingProps) {
  const { lang, t } = useLanguage();
  const page = getKeywordPage(slug);
  const pick = (value: Localized) => (lang === 'bm' ? value.bm : value.en);
  const language = lang === 'bm' ? 'ms-MY' : 'en-MY';
  const path = `/${slug}/`;
  const canonical = `https://www.mrcuci.me${path}`;

  usePageSeo({
    title: page ? pick(page.title) : 'MrCuci',
    description: page ? pick(page.description) : '',
    path,
    language,
    keywords: page ? page.metaKeywords : [],
    ogType: 'article',
    schema: page
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              '@id': `${canonical}#breadcrumb`,
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: t('Home', 'Utama'), item: 'https://www.mrcuci.me/' },
                { '@type': 'ListItem', position: 2, name: page.keyword, item: canonical },
              ],
            },
            {
              '@type': 'WebPage',
              '@id': `${canonical}#webpage`,
              url: canonical,
              name: pick(page.title),
              description: pick(page.description),
              inLanguage: language,
              isPartOf: { '@id': 'https://www.mrcuci.me/#website' },
              breadcrumb: { '@id': `${canonical}#breadcrumb` },
              mainEntity: { '@id': `${canonical}#article` },
              dateModified: '2026-08-13',
              keywords: page.metaKeywords.join(', '),
              about: {
                '@type': 'Thing',
                name: page.keyword,
                description: pick(page.schemaCategory),
              },
              audience: {
                '@type': 'PeopleAudience',
                suggestedMinAge: 18,
                geographicArea: { '@type': 'Country', name: 'Malaysia' },
              },
            },
            {
              '@type': 'Article',
              '@id': `${canonical}#article`,
              url: canonical,
              headline: pick(page.h1),
              name: pick(page.title),
              description: pick(page.description),
              articleSection: pick(page.schemaCategory),
              keywords: page.metaKeywords.join(', '),
              inLanguage: language,
              datePublished: '2026-08-13',
              dateModified: '2026-08-13',
              mainEntityOfPage: { '@id': `${canonical}#webpage` },
              author: { '@id': 'https://www.mrcuci.me/#organization' },
              publisher: { '@id': 'https://www.mrcuci.me/#organization' },
              about: { '@type': 'Thing', name: page.keyword },
              audience: {
                '@type': 'PeopleAudience',
                suggestedMinAge: 18,
                geographicArea: { '@type': 'Country', name: 'Malaysia' },
              },
            },
            {
              '@type': 'FAQPage',
              '@id': `${canonical}#faq`,
              url: `${canonical}#keyword-faq`,
              inLanguage: language,
              isPartOf: { '@id': `${canonical}#webpage` },
              mainEntity: page.faqs.map((faq) => ({
                '@type': 'Question',
                name: pick(faq.question),
                acceptedAnswer: { '@type': 'Answer', text: pick(faq.answer) },
              })),
            },
          ],
        }
      : { '@context': 'https://schema.org', '@type': 'WebPage' },
  });

  if (!page) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#080000] font-sans">
      <Navbar />

      <main className="pt-20">
        <section
          className="border-b border-[#d4a017]/10 py-14 md:py-20"
          style={{ background: 'linear-gradient(135deg, #0d0000 0%, #1a0303 55%, #0d0000 100%)' }}
        >
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <h1 className="mb-5 text-3xl font-black leading-tight text-white md:text-5xl">
              <LinkedText>{pick(page.h1)}</LinkedText>
            </h1>
            <div className="max-w-3xl space-y-4 text-sm leading-[1.85] text-gray-300 md:text-base">
              {page.intro.map((paragraph) => (
                <p key={paragraph.en}><LinkedText>{pick(paragraph)}</LinkedText></p>
              ))}
            </div>
          </div>
        </section>

        <nav
          aria-label={t('Page contents', 'Kandungan halaman')}
          className="border-b border-[#d4a017]/10"
          style={{ background: '#0d0101' }}
        >
          <div className="mx-auto max-w-4xl px-4 py-7 md:px-6">
            <h2 className="mb-4 text-lg font-black text-white">
              {t('On this page', 'Dalam halaman ini')}
            </h2>
            <ol className="grid gap-2 text-sm sm:grid-cols-2">
              {page.sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="block rounded-lg border border-[#d4a017]/10 px-4 py-3 text-gray-400 transition-colors hover:border-[#d4a017]/40 hover:text-[#d4a017]"
                  >
                    {pick(section.title)}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#keyword-faq"
                  className="block rounded-lg border border-[#d4a017]/10 px-4 py-3 text-gray-400 transition-colors hover:border-[#d4a017]/40 hover:text-[#d4a017]"
                >
                  {t(`${page.keyword} FAQ`, `FAQ ${page.keyword}`)}
                </a>
              </li>
            </ol>
          </div>
        </nav>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-4xl space-y-8 px-4 md:px-6">
            {page.sections.map((section) => (
              <article
                id={section.id}
                key={section.id}
                className="scroll-mt-24 rounded-2xl border border-[#d4a017]/10 p-6 md:p-8"
                style={{ background: 'linear-gradient(135deg, #180303, #1e0404)' }}
              >
                <h2 className="mb-4 text-xl font-black text-white md:text-2xl">{pick(section.title)}</h2>
                <div className="mb-6 space-y-3 text-sm leading-[1.85] text-gray-400">
                  {section.intro.map((paragraph) => (
                    <p key={paragraph.en}><LinkedText>{pick(paragraph)}</LinkedText></p>
                  ))}
                </div>
                <div className="space-y-6">
                  {section.blocks.map((block) => (
                    <section key={block.heading.en}>
                      <h3 className="mb-2 text-lg font-bold text-[#d4a017]">{pick(block.heading)}</h3>
                      <div className="space-y-3 text-sm leading-[1.85] text-gray-400">
                        {block.paragraphs.map((paragraph) => (
                          <p key={paragraph.en}><LinkedText>{pick(paragraph)}</LinkedText></p>
                        ))}
                      </div>
                      {block.bullets && block.listLabel && (
                        <>
                          <h4 className="mb-2 mt-4 text-base font-semibold text-white">
                            {pick(block.listLabel)}
                          </h4>
                          <ul className="list-disc space-y-2 pl-5 text-sm leading-[1.75] text-gray-400">
                            {block.bullets.map((bullet) => (
                              <li key={bullet.en}><LinkedText>{pick(bullet)}</LinkedText></li>
                            ))}
                          </ul>
                        </>
                      )}
                    </section>
                  ))}
                </div>
              </article>
            ))}

            <article
              id="keyword-faq"
              className="scroll-mt-24 rounded-2xl border border-[#d4a017]/10 p-6 md:p-8"
              style={{ background: 'linear-gradient(135deg, #180303, #1e0404)' }}
            >
              <h2 className="mb-5 text-xl font-black text-white md:text-2xl">
                {t(`${page.keyword} FAQ`, `FAQ ${page.keyword}`)}
              </h2>
              <div className="space-y-6">
                {page.faqs.map((faq) => (
                  <section key={faq.question.en}>
                    <h3 className="mb-2 text-lg font-bold text-[#d4a017]">{pick(faq.question)}</h3>
                    <p className="text-sm leading-[1.85] text-gray-400"><LinkedText>{pick(faq.answer)}</LinkedText></p>
                  </section>
                ))}
              </div>
            </article>

            <section
              className="rounded-2xl border border-[#d4a017]/20 p-6 md:p-8"
              style={{ background: 'linear-gradient(135deg, #200404, #2a0505)' }}
            >
              <h2 className="mb-4 text-2xl font-black text-white">
                <LinkedText>{t(`Open MrCuci after reading ${page.keyword}`, `Buka MrCuci selepas membaca ${page.keyword}`)}</LinkedText>
              </h2>
              <p className="mb-6 text-sm leading-[1.85] text-gray-400">
                {t(
                  'Register only if you are 18 or older, understand the risk, and can keep play inside a personal budget.',
                  'Daftar hanya jika anda berumur 18 tahun ke atas, memahami risiko, dan boleh mengekalkan permainan dalam bajet peribadi.'
                )}
              </p>
              <div className="flex flex-wrap gap-3">
                <RegisterLink
                  source={`keyword_${page.slug}_register`}
                  label={t('Register at MrCuci', 'Daftar di MrCuci')}
                  className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #cc1100, #ff2200)' }}
                >
                  <span>{t('Register at MrCuci', 'Daftar di MrCuci')}</span>
                  <i className="ri-arrow-right-line" />
                </RegisterLink>
                <Link
                  to="/bonus/"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#d4a017]/40 px-6 py-3 text-sm font-bold text-[#d4a017]"
                >
                  {t('Current bonuses', 'Bonus semasa')}
                </Link>
                <Link
                  to="/responsible-gaming/"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#d4a017]/40 px-6 py-3 text-sm font-bold text-[#d4a017]"
                >
                  {t('Responsible Gaming', 'Permainan Bertanggungjawab')}
                </Link>
              </div>
              <p className="mt-6 text-xs font-bold text-gray-500">
                {t('18+ only. Gambling involves financial risk.', '18+ sahaja. Perjudian melibatkan risiko kewangan.')}
              </p>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
